import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Leaf, User, Phone, Calendar, Save, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone_number: "", gender: "", date_of_birth: "" });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/auth/profile`);
        setProfile(res.data);
        setForm({
          name: res.data.name || "",
          phone_number: res.data.phone_number || "",
          gender: res.data.gender || "",
          date_of_birth: res.data.date_of_birth || "",
        });
      } catch {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, form);
      toast.success("Profile updated!");
    } catch {
      toast.error("Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F6F1]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E5D4C1]/30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2 text-[#2D3A2D]">
            <ArrowLeft className="w-4 h-4" />
            <Leaf className="w-5 h-5 text-[#7A9B7E]" />
            <span className="font-serif text-lg font-medium">My Story</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-white shadow-lg object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full mx-auto mb-4 bg-[#9FB89F]/20 flex items-center justify-center border-4 border-white shadow-lg">
              <User className="w-8 h-8 text-[#7A9B7E]" />
            </div>
          )}
          <h1 className="font-serif text-3xl font-medium text-[#2D3A2D]" data-testid="profile-heading">Your Profile</h1>
          <p className="font-sans text-sm text-[#7A8A7A] mt-2">{profile?.email}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-[#E5D4C1]/30 p-8 space-y-6" data-testid="profile-form">
          {/* Name */}
          <div>
            <label className="block font-sans text-sm font-medium text-[#4A5D4A] mb-2">Full Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#D4C9B8]/50 bg-[#F8F6F1] font-sans text-[#2D3A2D] focus:outline-none focus:ring-2 focus:ring-[#7A9B7E]/40"
              data-testid="profile-name"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block font-sans text-sm font-medium text-[#4A5D4A] mb-2">
              <Phone className="w-4 h-4 inline mr-1" /> Phone Number
              <span className="text-[#9FB89F] ml-1">(optional)</span>
            </label>
            <input
              type="tel"
              value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              placeholder="+91 98765 43210"
              className="w-full px-4 py-3 rounded-xl border border-[#D4C9B8]/50 bg-[#F8F6F1] font-sans text-[#2D3A2D] focus:outline-none focus:ring-2 focus:ring-[#7A9B7E]/40"
              data-testid="profile-phone"
            />
          </div>

          {/* Gender */}
          <div>
            <label className="block font-sans text-sm font-medium text-[#4A5D4A] mb-2">
              Gender <span className="text-[#9FB89F] ml-1">(optional)</span>
            </label>
            <select
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#D4C9B8]/50 bg-[#F8F6F1] font-sans text-[#2D3A2D] focus:outline-none focus:ring-2 focus:ring-[#7A9B7E]/40"
              data-testid="profile-gender"
            >
              <option value="">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="non-binary">Non-binary</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block font-sans text-sm font-medium text-[#4A5D4A] mb-2">
              <Calendar className="w-4 h-4 inline mr-1" /> Date of Birth
              <span className="text-[#9FB89F] ml-1">(optional)</span>
            </label>
            <input
              type="date"
              value={form.date_of_birth}
              onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-[#D4C9B8]/50 bg-[#F8F6F1] font-sans text-[#2D3A2D] focus:outline-none focus:ring-2 focus:ring-[#7A9B7E]/40"
              data-testid="profile-dob"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#4A5D4A] text-white font-sans font-medium hover:bg-[#2D3A2D] transition-all disabled:opacity-50"
            data-testid="profile-save-btn"
          >
            {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
